import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/cn";

import { Icon, type IconName } from "./Icon";

const tagVariants = cva(
  // Base styles
  "inline-flex items-center justify-center rounded-xl fs-100 leading-none! text-trim font-medium shrink-0 gap-1",
  {
    variants: {
      variant: {
        // Positive - зелёный (success)
        positive: "bg-success/17 text-success",
        // Negative - красный (danger)
        negative: "bg-danger/10 text-danger",
        // Neutral - серый
        neutral: "bg-neutral-200/5 text-neutral-200/50",
      },
      tagType: {
        text: "p-2",
        icon: "py-1.5 px-2",
        iconText: "p-1.5 [&_svg]:size-3",
      },
    },
    defaultVariants: {
      variant: "neutral",
      tagType: "text",
    },
  }
);

interface TagProps
  extends
    Omit<React.ComponentProps<"span">, "children">,
    Omit<VariantProps<typeof tagVariants>, "tagType"> {
  /** Text content */
  children?: React.ReactNode;
  /** Icon name (displayed before text) */
  icon?: IconName;
}

/**
 * Tag component for status indicators
 *
 * @example
 * // Text only
 * <Tag variant="positive">В работе</Tag>
 *
 * // Icon + text
 * <Tag variant="negative" icon="x">Ошибка</Tag>
 *
 * // Icon only
 * <Tag variant="neutral" icon="check-circle" />
 */
const Tag = ({ className, variant, icon, children, ...props }: TagProps) => {
  const tagType = icon ? (children ? "icon" : "iconText") : "text";

  return (
    <span
      data-slot="tag"
      className={cn(tagVariants({ variant, tagType }), className)}
      {...props}
    >
      {icon && (
        <Icon
          name={icon}
          className="size-3"
        />
      )}
      {children}
    </span>
  );
};

export { Tag, tagVariants };
